import React, { useEffect, useState } from 'react'
import { tr } from '../../utils/translations/translate'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from '../../redux/app/app-store'
import {
    TEXT_GNR_BNR,
    TEXT_SERIALNUMBER,
    TEXT_INVOICE_RECEIVER,
    TEXT_OWNER,
    TEXT_COUNT_UTILITY_UNITS,
    TEXT_CONTRACTTYPE,
    TEXT_LAST_INVOICING,
    TEXT_CONTRACT_USE_CUSTOM_TERM,
    TEXT_CONTRACT_USE_CUSTOM_TERMS_TOOLTIP,
    TEXT_ORGANIZATION_NUMBER,
    TEXT_NAME,
    TEXT_ORGANIZATIONS_COUNT,
} from '../../utils/translations/keys'
import {
    makeStyles,
    Theme,
    Grid,
    Paper,
    FormControlLabel,
    Typography,
    Tooltip,
} from '@material-ui/core'
import { Contract } from 'models/contract'
import { getContractMatrikkelNumberLong, getContractOrganizationNumber } from 'utils/contract-utils'
import { Address } from 'models/contract'
import { toShortDate } from 'utils/time/timeUtil'
import Switch from '@material-ui/core/Switch'
import { ProductPeriodSelectionComponent } from 'pages/product-page/product-period-selection'
import HelpIcon from '@material-ui/icons/Help'


const useStyles = makeStyles((theme: Theme) => {
    return {
        headerWrapper: {
            padding: '18px 31px',
            justifyContent: 'space-between',
        },
        formContainer: {
            padding: '30px',
        },
        titleCase: {
            textTransform: 'capitalize',
            overflowWrap: 'break-word',
        },
        title: {
            fontWeight: 'bold',
        },
        customTermsHeaderWrapper: {
            padding: '0px 31px',
            justifyContent: 'space-between',
        },
        helpIcon: {
            color: 'rgba(0, 0, 0, 0.54)',
        },
        customTermsControlWrapper: {
            alignItems: 'center',
            display: 'flex',
        },
        serialNumberEdit: {
            width: 120,
            display: 'flex',
            alignItems: 'center',
        },
        editIcon: {
            padding: '0',
            marginLeft: '12px',
        },
        doneIcon: {
            padding: '0',
        },
        errorText: {
            color: '#f44336',
            margin: 0,
            fontSize: '0.75rem',
            marginTop: '8px',
            minHeight: '1em',
            textAlign: 'left',
            fontWeight: 400,
            lineHeight: '1em',
            letterSpacing: '0.03333em',
        },
    }
})

export const ContractMainInfo: React.FC<{
    handleUseCustomTermChanged: any
    handleSelectedPeriodTypeChanged: any
    handleSelectedPeriodsChanged: any
}> = ({
    handleUseCustomTermChanged,
    handleSelectedPeriodTypeChanged,
    handleSelectedPeriodsChanged,
}) => {
        const styles = useStyles()
        const tenantConfig = useSelector((state: AppState) => state.config.tenantConfig)
        const contract = useSelector((state: AppState) => state.contracts.selectedContract)
        const isNsoTenant = useSelector((state: AppState) => state.config.tenantConfig?.isNsoTenant)


        const renderStyledField = (fieldName: string, fieldValue: any, className?: string, index: number = 0) => {
            return (
                <Grid item md={4} key={index}>
                    <div>
                        <b>{tr(fieldName) && tr(fieldName).length > 0 ? tr(fieldName) : fieldName}</b>
                    </div>
                    <div className={className}>{fieldValue}</div>
                </Grid>
            )
        }

        const renderField = (fieldName: string, fieldValue: any, index: number = 0) => {
            return renderStyledField(fieldName, fieldValue, '', index)
        }

        const getAddressFormatted = (emptyPlaceholder: string, address?: Address) => {
            var addressStr = address
                ? `${address?.streetAddress ? `${address?.streetAddress}, ` : ''}${address?.postalCode ?? '-'} ${address?.city ?? ''
                }`
                : emptyPlaceholder
            return addressStr
        }

        const onHandleUseCustomTermChanged = (useCustomTerms: boolean) => {
            handleUseCustomTermChanged(useCustomTerms)
        }

        const selectedPeriodTypeChanged = (periodType: string) => {
            handleSelectedPeriodTypeChanged(periodType)
        }

        const selectedPeriodsValueOnChange = (selectedPeriods: number[]) => {
            handleSelectedPeriodsChanged(selectedPeriods)
        }

        return (
            <Paper className={styles.formContainer}>
                {isNsoTenant ? (
                    <Grid container className={styles.headerWrapper} spacing={2}>
                        {renderField(TEXT_ORGANIZATION_NUMBER, `${getContractOrganizationNumber(contract)}`, 0)}
                        <Grid item md={4} key={1}>
                            <div>
                                <b>{tr(TEXT_SERIALNUMBER)}</b>
                            </div>
                            <div>
                                <div>
                                    <span>{contract.serialNumber}</span>
                                </div>
                            </div>
                        </Grid>
                        {renderField(
                            TEXT_ORGANIZATIONS_COUNT,
                            contract.residentialInfo ? contract.residentialInfo.length : 0,
                            2,
                        )}
                        {renderStyledField(
                            TEXT_INVOICE_RECEIVER,
                            `${contract.ownerFullName || ''} - ${contract.invoiceRecipientAddress?.streetAddress ?? contract.ownerAddress?.streetAddress}`,
                            styles.titleCase,
                            3,
                        )}
                        {renderField(TEXT_NAME, contract.ownerFullName, 4)}
                    </Grid>
                ) : (
                    <Grid container className={styles.headerWrapper} spacing={2}>
                        {renderField(TEXT_GNR_BNR, `${getContractMatrikkelNumberLong(contract)}`, 0)}
                        <Grid item md={4} key={1}>
                            <div>
                                <b>{tr(TEXT_SERIALNUMBER)}</b>
                            </div>
                            <div>
                                <div>
                                    <span>{contract.serialNumber}</span>
                                </div>
                            </div>
                        </Grid>
                        {!contract.isProductBasedContract &&
                            renderField(
                                TEXT_COUNT_UTILITY_UNITS,
                                contract.residentialInfo ? contract.residentialInfo.length : 0,
                                2,
                            )}
                        {renderStyledField(
                            TEXT_OWNER,
                            `${contract.ownerFullName?.toLowerCase() || ''} - ${getAddressFormatted(
                                '',
                                contract.ownerAddress,
                            )}`,
                            styles.titleCase,
                            3,
                        )}
                        {renderStyledField(
                            TEXT_INVOICE_RECEIVER,
                            `${(contract.invoiceRecipientFullName ?? contract.ownerFullName)?.toLowerCase() || ''
                            } - ${getAddressFormatted('', contract.invoiceRecipientAddress ?? contract.ownerAddress)}`,
                            styles.titleCase,
                            4,
                        )}
                        {tenantConfig?.isContractBasedInvoicing &&
                            renderStyledField(
                                TEXT_LAST_INVOICING,
                                `${toShortDate(contract.lastSyncedDate) || '-'}`,
                                styles.titleCase,
                                5,
                            )}
                    </Grid>
                )}
                {!tenantConfig?.isOldTenant && (
                    <Grid container className={styles.customTermsHeaderWrapper} spacing={2}>
                        <Grid item md={12}>
                            <div className={styles.customTermsControlWrapper}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={contract.useCustomTerms}
                                            onChange={(event) => onHandleUseCustomTermChanged(event.target.checked)}
                                            name="useCustomTermSwitch"
                                        />
                                    }
                                    label={
                                        <Typography className={styles.title}>
                                            {tr(TEXT_CONTRACT_USE_CUSTOM_TERM)}
                                        </Typography>
                                    }
                                />
                                <Tooltip title={tr(TEXT_CONTRACT_USE_CUSTOM_TERMS_TOOLTIP)} placement="right-start">
                                    <HelpIcon className={styles.helpIcon}></HelpIcon>
                                </Tooltip>
                            </div>
                        </Grid>
                    </Grid>
                )}
                {contract.useCustomTerms && (
                    <ProductPeriodSelectionComponent
                        selectedPeriodsValue={contract.selectedPeriods}
                        periodType={contract.periodType}
                        selectedPeriodsValueOnChange={selectedPeriodsValueOnChange}
                        selectedPeriodTypeChanged={selectedPeriodTypeChanged}
                    ></ProductPeriodSelectionComponent>
                )
                }
            </Paper >
        )
    }
